import type { AdminInvite, InvitedPerson, RoomType } from '../../types'

/** The counts the caterers and the venue will ask for, derived from what is loaded. */
export function ConsoleStats({
  invites,
  roomTypes,
}: {
  invites: readonly AdminInvite[]
  roomTypes: readonly RoomType[]
}) {
  const people = invites.flatMap((i) => i.people)
  const count = (fn: (p: InvitedPerson) => boolean) => people.filter(fn).length
  const confirmed = (fn: (p: InvitedPerson) => boolean) =>
    count((p) => fn(p) && p.response === 'yes')

  const day = (p: InvitedPerson) => p.invite_type === 'day'
  const evening = (p: InvitedPerson) => p.invite_type === 'evening'
  const child = (p: InvitedPerson) => p.is_child

  const totals = [
    { label: 'Invites', value: invites.length },
    { label: 'Guests', value: people.length },
    { label: 'Yes', value: count((p) => p.response === 'yes') },
    { label: 'No', value: count((p) => p.response === 'no') },
    { label: 'Waiting', value: count((p) => p.response === 'waiting') },
  ]

  /*
   * Confirmed against invited, rather than two tiles apiece.
   *
   * It is the same six numbers either way, but the pair is the question actually
   * being asked - "how many of the people we asked have said yes" - and eleven
   * separate figures is a wall to read rather than a summary.
   *
   * Day and evening are the invite each guest holds, so they do not overlap and
   * they add up to Guests. Children do overlap both: a child is a guest the
   * venue counts differently, not a third audience.
   */
  const audiences = [
    { label: 'Day', of: count(day), value: confirmed(day) },
    { label: 'Evening', of: count(evening), value: confirmed(evening) },
    { label: 'Children', of: count(child), value: confirmed(child) },
  ]

  /*
   * Rooms in Horsley Towers, in the two forms that get asked about.
   *
   * Taken against capacity is the number the venue wants; paid against taken is
   * the one worth chasing. Both are counted from the invites and the room types
   * rather than kept anywhere, so they cannot fall out of step with the bookings
   * they describe.
   */
  const taken = invites.filter((invite) => invite.room !== null).length
  const capacity = roomTypes.reduce((sum, room) => sum + room.capacity, 0)
  const paid = invites.filter((invite) => invite.room?.paid_at != null).length

  const rooms = [
    { label: 'Rooms', of: capacity, value: taken, hint: 'taken of ' + capacity },
    { label: 'Paid', of: taken, value: paid, hint: 'paid of taken' },
  ]

  return (
    <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">
      {totals.map((tile) => (
        <Tile key={tile.label} label={tile.label}>
          {tile.value}
        </Tile>
      ))}
      {audiences.map((tile) => (
        <Tile key={tile.label} label={tile.label} hint="confirmed of invited">
          {tile.value}
          <span className="text-ink-soft font-sans text-sm"> of {tile.of}</span>
        </Tile>
      ))}
      {rooms.map((tile) => (
        <Tile key={tile.label} label={tile.label} hint={tile.hint}>
          {tile.value}
          <span className="text-ink-soft font-sans text-sm"> of {tile.of}</span>
        </Tile>
      ))}
    </dl>
  )
}

function Tile({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="border-ink/10 bg-parchment/50 rounded-md border px-3 py-2">
      <dt className="text-ink-soft text-[0.65rem] tracking-widest uppercase">
        {label}
        {/* Spelled out for a screen reader, since "2 of 4" alone does not say
            which way round it is. */}
        {hint !== undefined && <span className="sr-only"> — {hint}</span>}
      </dt>
      <dd className="font-display mt-0.5 text-2xl tabular-nums">{children}</dd>
      {hint !== undefined && (
        <p aria-hidden="true" className="text-ink-soft text-[0.6rem] tracking-wide">
          {hint}
        </p>
      )}
    </div>
  )
}
